import { Schema, Types, model, models, Model } from "mongoose";
import { NetworkSymbols } from "based-auth/networks";
import { SUB_BOARD_TITLE_LIMIT } from "based-shared/constants";
import { stringifyFields } from "based-shared/utils/stringify-fields";

export interface IFollower {
  _id: string;
  network: NetworkSymbols;
  address: string;
}

export interface ISubBoard extends Document {
  _id: string;
  chainId: number;
  account: string;
  title: string;
  logo: string;
  banner: string;
  description: string;
  fees: {
    listingFee: Types.Decimal128;
    listingReferralFee: Types.Decimal128;
    buyFeePer: Types.Decimal128;
    sellFeePer: Types.Decimal128;
    finalizeFeePer: Types.Decimal128;
  }[];
  isAllowed: boolean;
  followers: IFollower[];
  // Privacy and Access Control
  privacy: {
    mode: "public" | "private" | "request_to_join";
    allowRequests: boolean;
    requestsActiveUntil?: Date;
    requestTimeLimit?: number; // in hours
  };
  members: IFollower[]; // Approved members for private boards
  invitations: string[]; // Pending invitation IDs
  metaUri: string;
  telegram: string;
  twitter: string;
  gitbook: string;
  tiktok: string;
  youtube: string;
  createdAt: Date;
  updatedAt: Date;
}

const subBoardSchema = new Schema<ISubBoard>(
  {
    chainId: { type: Number, required: true },
    account: { type: String, required: true },
    title: {
      type: String,
      required: true,
      maxLength: SUB_BOARD_TITLE_LIMIT,
      match: /^[a-zA-Z0-9]+$/,
    },
    logo: {
      type: String,
      default: "",
    },
    banner: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    telegram: {
      type: String,
      default: "",
    },
    twitter: {
      type: String,
      default: "",
    },
    gitbook: {
      type: String,
      default: "",
    },
    tiktok: {
      type: String,
      default: "",
    },
    youtube: {
      type: String,
      default: "",
    },
    fees: [
      {
        listingFee: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
        listingReferralFee: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
        buyFeePer: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
        sellFeePer: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
        finalizeFeePer: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
      },
    ],
    isAllowed: {
      type: Boolean,
      default: true,
    },
    followers: [
      {
        network: {
          type: String,
          required: true,
        },
        address: {
          type: String,
          required: true,
        },
      },
    ],
    // Privacy and Access Control
    privacy: {
      mode: {
        type: String,
        enum: ["public", "private", "request_to_join"],
        default: "public",
      },
      allowRequests: {
        type: Boolean,
        default: true,
      },
      requestsActiveUntil: {
        type: Date,
        required: false,
      },
      requestTimeLimit: {
        type: Number,
        required: false,
      },
    },
    members: [
      {
        network: {
          type: String,
          required: true,
        },
        address: {
          type: String,
          required: true,
        },
      },
    ],
    invitations: [
      {
        type: String,
        ref: "BoardInvitation",
      },
    ],
    metaUri: {
      type: String,
      required: true,
    },
  },
  {
    timestamps: true,
    toObject: {
      getters: true,
      versionKey: false,
      transform: (doc, ret: any) => {
        ret.id = ret._id.toString();
        delete ret._id;
        delete ret.__v;

        return stringifyFields(ret);
      },
    },
  }
);

subBoardSchema.index({ title: 1 });
subBoardSchema.index({ chainId: 1, title: 1 }, { unique: true });
subBoardSchema.index({ chainId: 1, account: 1, title: 1 });
subBoardSchema.index({ "followers.address": 1, "followers.network": 1 });

export const SubBoardModel: Model<ISubBoard> =
  models?.SubBoard || model<ISubBoard>("SubBoard", subBoardSchema);
